/**
 * FirstSteps · «Первые шаги» — the action checklist of the onboarding.
 * slot_onboarding_redesign OB.3 (23 MAY 2026).
 *
 * Post-T00 rework (slot_t00_ftue_rewrite · Genie 7 JUN 2026): the tutorial now
 * arms AFTER the first ticket is closed (T00 сам учит механике закрытия), so
 * the checklist guides the next real actions instead of re-teaching T00:
 * взять следующий тикет → закрыть его → глоссарий → помодоро.
 *
 * **Step completion is mostly derived, not stored.** Steps 1-2 read live,
 * read-only from progress (step 1 = any ticket taken past the intro T00), step 4
 * from `progress.achievements.pomodoroSessions`. Only step 3 (open Glossary) is a
 * click-marked, onboarding-local flag, because glossary open is ephemeral App
 * state with no `progress` signal.
 *
 * Terminal states (both call `onHide`): 5/5 → celebration; or the explicit
 * «Уже понятно» button at any time (Genie OB.2 Q3).
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Buddy } from '@/components/Buddy';
import { useProgress } from '@/contexts/ProgressContext';

/** Intro/tutorial ticket — closing it arms the checklist. Step «возьми следующий
 *  тикет» completes on ANY ticket beyond this one (see below). */
const INTRO_TICKET = 'T00';

interface FirstStepsProps {
  /** Open the active sprint's Board (App.tsx `goSprint`). */
  onOpenBoard: () => void;
  /** Open the course map / sprints timeline (App.tsx `goSprints`). */
  onOpenMap: () => void;
  /** Open the Glossary overlay (App.tsx `openGlossary`). */
  onOpenGlossary: () => void;
  /** «Заглянул в Карту» — derived from the map first-visit hint dismissal. */
  mapVisited: boolean;
  /** Glossary step done flag (onboarding-local). */
  glossaryStepDone: boolean;
  /** Mark glossary step done. */
  onGlossaryStep: () => void;
  /** Dismiss the checklist permanently. */
  onHide: () => void;
}

interface Step {
  key: string;
  label: ReactNode;
  done: boolean;
  /** Click handler when this is the active step. Undefined → not actionable. */
  action?: () => void;
}

export function FirstSteps({
  onOpenBoard,
  onOpenMap,
  onOpenGlossary,
  mapVisited,
  glossaryStepDone,
  onGlossaryStep,
  onHide,
}: FirstStepsProps) {
  const { progress } = useProgress();
  const [collapsed, setCollapsed] = useState(false);

  // --- Derive steps read-only from progress ------------------------------
  // Done once the user picks up ANY lesson ticket past the intro T00. Deriving off
  // a fixed id (was 'MS-01') rotted when S01's tickets were re-ordered — MS-00 got
  // inserted after T00, so the real "next" ticket changed and the step never
  // ticked. "Anything but T00" survives content reshuffles.
  const openedNext =
    (!!progress.last.ticketId && progress.last.ticketId !== INTRO_TICKET) ||
    Object.keys(progress.tickets).some((id) => id !== INTRO_TICKET);
  const pomodoroDone = (progress.achievements?.pomodoroSessions?.length ?? 0) > 0;

  const steps: Step[] = [
    {
      key: 'board',
      label: 'Открой Доску и возьми следующий тикет',
      done: openedNext,
      action: onOpenBoard,
    },
    {
      key: 'map',
      label: 'Загляни в Карту — весь маршрут курса',
      done: mapVisited,
      action: onOpenMap,
    },
    {
      key: 'gloss',
      label: (
        <>
          Загляни в Глоссарий <span className="onb-kbd">⌘K</span>
        </>
      ),
      done: glossaryStepDone,
      action: () => {
        onGlossaryStep();
        onOpenGlossary();
      },
    },
    {
      key: 'pomo',
      label: 'Запусти Помодоро на один подход — кнопка справа внизу',
      done: pomodoroDone,
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const allDone = doneCount === total;
  const pct = Math.round((doneCount / total) * 100);
  const activeIdx = steps.findIndex((s) => !s.done);

  // --- 5/5 celebration ---------------------------------------------------
  if (allDone) {
    return (
      <div className="onb-card onb-steps-done" role="region" aria-label="Первые шаги — готово">
        <div className="onb-done-title">Рабочий ритм пойман</div>
        <div className="onb-done-sub">
          Тикеты, глоссарий, помодоро — дальше просто работа. Перепройти знакомство
          можно из настроек профиля.
        </div>
        <button
          type="button"
          className="onb-btn onb-btn-primary"
          style={{ marginTop: 12 }}
          onClick={onHide}
        >
          Закрыть ✓
        </button>
      </div>
    );
  }

  // --- collapsed strip ---------------------------------------------------
  if (collapsed) {
    return (
      <button
        type="button"
        className="onb-card onb-steps-strip"
        onClick={() => setCollapsed(false)}
        aria-label={`Первые шаги — ${doneCount} из ${total}, развернуть`}
      >
        <span className="onb-buddy" aria-hidden="true">
          <Buddy size={20} />
        </span>
        <span className="onb-strip-label">Первые шаги</span>
        <span className="onb-meta">
          {doneCount}/{total} ▸
        </span>
      </button>
    );
  }

  // --- full checklist ----------------------------------------------------
  return (
    <div className="onb-card onb-steps" role="region" aria-label="Первые шаги">
      <div className="onb-steps-head">
        <span className="onb-buddy" aria-hidden="true">
          <Buddy size={22} />
        </span>
        <h2 className="onb-title">
          Первые <span className="onb-accent">шаги</span>
        </h2>
        <span className="onb-meta onb-steps-count">
          {doneCount} / {total}
        </span>
        <button
          type="button"
          className="onb-collapse-btn"
          onClick={() => setCollapsed(true)}
          aria-label="Свернуть чеклист"
        >
          ▾
        </button>
      </div>

      <div
        className="onb-bar"
        role="progressbar"
        aria-valuenow={doneCount}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label="Прогресс знакомства"
      >
        <div
          className={`onb-bar-fill${pct === 100 ? ' onb-bar-full' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="onb-step-list">
        {steps.map((s, i) => {
          if (s.done) {
            return (
              <div key={s.key} className="onb-step onb-step-done">
                <span className="onb-step-mark" aria-hidden="true">
                  ✓
                </span>
                <span>{s.label}</span>
              </div>
            );
          }
          // Genie 7 JUN 2026: ЛЮБОЙ невыполненный шаг с action кликабелен —
          // раньше кнопкой был только активный, и тап по «Карте»/«Глоссарию»
          // вне порядка молчал (выглядело как «не работает на планшете»).
          // Активный сохраняет подсветку и стрелку.
          if (s.action) {
            const isActive = i === activeIdx;
            return (
              <button
                key={s.key}
                type="button"
                className={`onb-step${isActive ? ' onb-step-active' : ''}`}
                onClick={s.action}
              >
                <span className="onb-step-mark" aria-hidden="true" />
                <span>{s.label}</span>
                {isActive && (
                  <span className="onb-step-go" aria-hidden="true">
                    →
                  </span>
                )}
              </button>
            );
          }
          return (
            <div key={s.key} className="onb-step">
              <span className="onb-step-mark" aria-hidden="true" />
              <span>{s.label}</span>
            </div>
          );
        })}
      </div>

      <div className="onb-atelier-note">
        Артель для практики откроется, когда дойдёшь — пока можно просто заглянуть.
      </div>

      <div className="onb-steps-foot">
        <span className="onb-foot-note">Закроется на 4/4 — или сразу:</span>
        <button
          type="button"
          className="onb-btn onb-btn-ghost"
          style={{ marginLeft: 'auto' }}
          onClick={onHide}
        >
          Уже понятно
        </button>
      </div>
    </div>
  );
}
